import React, { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from "react";
import ReactDOM from "react-dom";
import { Box, Button, Sheet, Stack, Typography } from "@mui/joy";
import { useTutorialContext } from "./TutorialContext";
import { useTutorialActions } from "./hooks";
import { TutorialPlacement } from "./types";

// ─── Layout constants ────────────────────────────────────────────────────────

const SP = 8;
const GAP = 14;
const TW = 288;
const EP = 12;
const AS = 9;

const SURFACE = "var(--joy-palette-background-surface)";
const DIVIDER = "var(--joy-palette-divider)";

// ─── Layout computation ───────────────────────────────────────────────────────

type ResolvedPlacement = "top" | "bottom" | "left" | "right";

interface Layout {
  pl: ResolvedPlacement;
  tooltipTop?: number;
  tooltipBottom?: number;
  tooltipLeft: number;
  arrowH?: number;
  arrowV?: number;
}

function resolvedPlacement(
  placement: TutorialPlacement | undefined,
  rect: DOMRect
): ResolvedPlacement {
  if (placement && placement !== "auto") return placement;
  return window.innerHeight - rect.bottom > rect.top ? "bottom" : "top";
}

function computeLayout(
  rect: DOMRect,
  rawPlacement: TutorialPlacement | undefined
): Layout {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const clampH = (x: number) => Math.max(EP, Math.min(x, vw - TW - EP));
  const clampArrowH = (center: number, left: number) =>
    Math.max(AS + 4, Math.min(center - left - AS, TW - AS * 2 - 4));

  // Resolve initial preference, then auto-flip if the preferred side has no room.
  let pl = resolvedPlacement(rawPlacement, rect);
  if (pl === "right" && rect.right + SP + GAP + TW + EP > vw) {
    pl = vh - rect.bottom > rect.top ? "bottom" : "top";
  } else if (pl === "left" && rect.left - SP - GAP - TW < EP) {
    pl = vh - rect.bottom > rect.top ? "bottom" : "top";
  }

  if (pl === "bottom") {
    const left = clampH(cx - TW / 2);
    return { pl, tooltipTop: rect.bottom + SP + GAP, tooltipLeft: left, arrowH: clampArrowH(cx, left) };
  }
  if (pl === "top") {
    const left = clampH(cx - TW / 2);
    return { pl, tooltipBottom: vh - (rect.top - SP - GAP), tooltipLeft: left, arrowH: clampArrowH(cx, left) };
  }
  if (pl === "right") {
    const top = Math.max(EP, Math.min(cy - 60, vh - 140 - EP));
    return { pl, tooltipTop: top, tooltipLeft: rect.right + SP + GAP, arrowV: Math.max(AS + 4, Math.min(cy - top - AS, 100)) };
  }
  // left
  const top = Math.max(EP, Math.min(cy - 60, vh - 140 - EP));
  return { pl, tooltipTop: top, tooltipLeft: Math.max(EP, rect.left - SP - GAP - TW), arrowV: Math.max(AS + 4, Math.min(cy - top - AS, 100)) };
}

// ─── Overlay ─────────────────────────────────────────────────────────────────

const TutorialOverlay: React.FC = () => {
  const { state, getTargetElement } = useTutorialContext();
  const { dismissActive, nextStep } = useTutorialActions();
  const { activeHint, activeTour, activeTourStepIndex } = state;

  const element = activeHint ? getTargetElement(activeHint.targetId) : null;

  // Refs to DOM nodes — all positional updates go directly to these,
  // bypassing the React render cycle so scroll never lags.
  const spotlightRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const arrowBorderRef = useRef<HTMLDivElement>(null);
  const arrowFillRef = useRef<HTMLDivElement>(null);

  // Used to force a React re-render (which re-evaluates placement direction
  // and repaints the arrow) when the viewport changes size or the target
  // element crosses a placement boundary. forceCount changes on each call so
  // the useLayoutEffect below can include it as a dep and re-run.
  const [forceCount, forceUpdate] = useReducer((n: number) => n + 1, 0);

  // Tracks the placement used in the last React render. syncPositions compares
  // against this: if placement flipped (e.g. "top"→"bottom" as content loaded
  // above the target and pushed it down), a DOM-only update can't flip the arrow
  // direction — only a React re-render can. In that case we trigger forceUpdate
  // and bail early; the re-render seeds the correct arrow + positions.
  const lastRenderedPlRef = useRef<ResolvedPlacement | null>(null);

  // Direct DOM write — called on every scroll / resize / ResizeObserver event.
  const syncPositions = useCallback(() => {
    if (!element || !spotlightRef.current || !tooltipRef.current) return;

    const rect = element.getBoundingClientRect();
    const layout = computeLayout(rect, activeHint?.placement);

    // If placement flipped, the arrow element in the DOM is wrong — its direction
    // is baked into JSX and can only be fixed by a React re-render. Trigger one
    // and bail; the re-render's useLayoutEffect will re-call syncPositions.
    if (layout.pl !== lastRenderedPlRef.current) {
      lastRenderedPlRef.current = layout.pl;
      forceUpdate();
      return;
    }

    // Spotlight — viewport coordinates (fixed positioning) so it doesn't drift when
    // the page scrolls. getBoundingClientRect() already returns viewport coords.
    const ss = spotlightRef.current.style;
    ss.left = `${rect.left - SP}px`;
    ss.top = `${rect.top - SP}px`;
    ss.width = `${rect.width + SP * 2}px`;
    ss.height = `${rect.height + SP * 2}px`;

    // Tooltip wrapper
    const ts = tooltipRef.current.style;
    ts.left = `${layout.tooltipLeft}px`;
    if (layout.tooltipTop !== undefined) {
      ts.top = `${layout.tooltipTop}px`;
      ts.bottom = "";
    } else {
      ts.bottom = `${layout.tooltipBottom!}px`;
      ts.top = "";
    }

    // Arrow offsets (only the axis that changes matters)
    if (layout.arrowH !== undefined) {
      if (arrowBorderRef.current) arrowBorderRef.current.style.left = `${layout.arrowH - 1}px`;
      if (arrowFillRef.current) arrowFillRef.current.style.left = `${layout.arrowH}px`;
    }
    if (layout.arrowV !== undefined) {
      if (arrowBorderRef.current) arrowBorderRef.current.style.top = `${layout.arrowV - 1}px`;
      if (arrowFillRef.current) arrowFillRef.current.style.top = `${layout.arrowV}px`;
    }
  }, [element, activeHint?.placement, forceUpdate]);

  // Apply correct positions before the very first paint (avoids any flash).
  // forceCount is included so this re-runs after forceUpdate() triggers a
  // re-render: syncPositions itself hasn't changed, but the JSX arrow structure
  // has, so we must re-apply positions to match the new arrow geometry.
  useLayoutEffect(() => {
    syncPositions();
  }, [syncPositions, forceCount]);

  // Scroll: update DOM directly so the spotlight tracks without React re-renders.
  // Resize: also force a React re-render so the arrow direction is re-evaluated
  //         (placement can flip between right↔bottom when the viewport narrows).
  // ResizeObserver on the target element: tracks position during CSS transitions
  //   (e.g. sidebar collapse animates width, which changes the element's size,
  //   firing the observer each frame so the spotlight stays glued to the item).
  // ResizeObserver on documentElement: catches layout shifts caused by the
  //   sidebar spacer changing width, which moves content-area elements without
  //   changing their own size.
  useEffect(() => {
    if (!element) return;

    const onResize = () => {
      syncPositions();
      forceUpdate();
    };

    const ro = new ResizeObserver(syncPositions);
    ro.observe(element);
    ro.observe(document.documentElement);
    // Observe all ancestors so that async content loading above the target
    // (e.g. a loading spinner resolving to a taller section) fires syncPositions
    // even when the target element's own size hasn't changed.
    let node: Element | null = element.parentElement;
    while (node && node !== document.documentElement) {
      ro.observe(node);
      node = node.parentElement;
    }
    window.addEventListener("scroll", syncPositions, true);
    window.addEventListener("resize", onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", syncPositions, true);
      window.removeEventListener("resize", onResize);
    };
  }, [element, syncPositions, forceUpdate]);

  // Scroll target into view on activation.
  useEffect(() => {
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [element]);

  // Escape dismisses.
  useEffect(() => {
    if (!activeHint) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissActive();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [activeHint, dismissActive]);

  if (!activeHint || !element) return null;

  // Compute layout during render to seed initial inline styles.
  // getBoundingClientRect() on an already-mounted element is safe during render.
  const rect = element.getBoundingClientRect();
  const layout = computeLayout(rect, activeHint.placement);
  const { pl } = layout;

  // Keep lastRenderedPlRef in sync with the current render's placement so the
  // first syncPositions call (via useLayoutEffect) doesn't see a stale null and
  // fire a spurious forceUpdate. Mutating a ref during render is intentional here.
  lastRenderedPlRef.current = pl;
  const isLastStep = activeTour && activeTourStepIndex === activeTour.steps.length - 1;

  // Shared arrow base style for left/right placements (the dynamic axis is `top`)
  const arrowVBorderStyle = (side: "left" | "right"): React.CSSProperties => ({
    position: "absolute",
    top: layout.arrowV! - 1,
    [side]: -(AS + 1),
    width: 0, height: 0,
    borderTop: `${AS + 1}px solid transparent`,
    borderBottom: `${AS + 1}px solid transparent`,
    [side === "right" ? "borderLeft" : "borderRight"]: `${AS + 1}px solid ${DIVIDER}`,
  });
  const arrowVFillStyle = (side: "left" | "right"): React.CSSProperties => ({
    position: "absolute",
    top: layout.arrowV!,
    [side]: -(AS - 1),
    width: 0, height: 0,
    zIndex: 2,
    borderTop: `${AS}px solid transparent`,
    borderBottom: `${AS}px solid transparent`,
    [side === "right" ? "borderLeft" : "borderRight"]: `${AS}px solid ${SURFACE}`,
  });

  const overlay = (
    <>
      {/* Backdrop with spotlight cut-out via box-shadow. No CSS transition —
          positions are driven by syncPositions() to stay glued to the element. */}
      <div
        ref={spotlightRef}
        style={{
          position: "fixed",
          left: rect.left - SP,
          top: rect.top - SP,
          width: rect.width + SP * 2,
          height: rect.height + SP * 2,
          borderRadius: 6,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          outline: "2px solid var(--joy-palette-primary-400)",
          outlineOffset: 1,
          pointerEvents: "none",
          zIndex: 1300,
        }}
      />

      {/* Tooltip wrapper — position seeded here, then kept in sync via ref */}
      <div
        ref={tooltipRef}
        style={{
          position: "fixed",
          width: TW,
          zIndex: 1302,
          left: layout.tooltipLeft,
          ...(layout.tooltipTop !== undefined
            ? { top: layout.tooltipTop }
            : { bottom: layout.tooltipBottom }),
        }}
      >
        {/* ── Arrows ── */}
        {pl === "bottom" && (
          <>
            <div ref={arrowBorderRef} style={{ position: "absolute", top: -(AS + 1), left: layout.arrowH! - 1, width: 0, height: 0,
              borderLeft: `${AS + 1}px solid transparent`, borderRight: `${AS + 1}px solid transparent`,
              borderBottom: `${AS + 1}px solid ${DIVIDER}` }} />
            <div ref={arrowFillRef} style={{ position: "absolute", top: -(AS - 1), left: layout.arrowH!, width: 0, height: 0, zIndex: 2,
              borderLeft: `${AS}px solid transparent`, borderRight: `${AS}px solid transparent`,
              borderBottom: `${AS}px solid ${SURFACE}` }} />
          </>
        )}
        {pl === "top" && (
          <>
            <div ref={arrowBorderRef} style={{ position: "absolute", bottom: -(AS + 1), left: layout.arrowH! - 1, width: 0, height: 0,
              borderLeft: `${AS + 1}px solid transparent`, borderRight: `${AS + 1}px solid transparent`,
              borderTop: `${AS + 1}px solid ${DIVIDER}` }} />
            <div ref={arrowFillRef} style={{ position: "absolute", bottom: -(AS - 1), left: layout.arrowH!, width: 0, height: 0, zIndex: 2,
              borderLeft: `${AS}px solid transparent`, borderRight: `${AS}px solid transparent`,
              borderTop: `${AS}px solid ${SURFACE}` }} />
          </>
        )}
        {pl === "right" && (
          <>
            <div ref={arrowBorderRef} style={arrowVBorderStyle("left")} />
            <div ref={arrowFillRef} style={arrowVFillStyle("left")} />
          </>
        )}
        {pl === "left" && (
          <>
            <div ref={arrowBorderRef} style={arrowVBorderStyle("right")} />
            <div ref={arrowFillRef} style={arrowVFillStyle("right")} />
          </>
        )}

        {/* ── Tooltip card ── */}
        <Sheet
          variant="outlined"
          sx={{ borderRadius: "md", boxShadow: "md", position: "relative", zIndex: 1 }}
        >
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Typography level="title-sm">{activeHint.title}</Typography>

            <Typography level="body-sm" color="neutral">
              {activeHint.body}
            </Typography>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.25 }}>
              {activeTour ? (
                <Typography level="body-xs" color="neutral">
                  {activeTourStepIndex + 1} / {activeTour.steps.length}
                </Typography>
              ) : (
                <Box />
              )}
              <Stack direction="row" spacing={1}>
                {activeTour && (
                  <Button size="sm" variant="plain" color="neutral"
                    onClick={dismissActive}>
                    Skip tour
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={activeTour ? "solid" : "soft"}
                  color="primary"
                  onClick={activeTour ? nextStep : dismissActive}
                >
                  {activeTour ? (isLastStep ? "Finish" : "Next →") : "Got it"}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Sheet>
      </div>
    </>
  );

  return ReactDOM.createPortal(overlay, document.body);
};

export default TutorialOverlay;
